<template>
  <div class="w-100 tag-input">
    <label v-if="label">{{ label }}</label>

    <div
      class="d-flex flex-wrap align-items-center rounded row-gap-md"
      style="padding: 6px 12px"
      :class="{ error: isValidate, border: !hideBorder }"
      @click="focusInput"
    >
      <span
        v-for="(tag, index) in internalTags"
        :key="index"
        class="badge badge-custom-primary mr-2 d-flex align-items-center"
        :class="{ disabled: disabledFirstValue && index === 0 }"
        style="font-size: 14px"
      >
        {{ tag }}
        <button
          v-if="!disabledFirstValue || index > 0"
          type="button"
          class="btn btn-sm btn-link text-light p-0 ml-1"
          @click.stop="removeTag(index)"
        >
          &times;
        </button>
      </span>

      <input
        v-if="!disabled"
        ref="input"
        type="text"
        v-model="inputValue"
        @input="onInput"
        @focus="onFocus"
        @keydown.enter.prevent="onEnter"
        @keydown.tab.prevent="onEnter"
        @keydown.esc.prevent="closeDropdown"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        class="border-0 flex-grow-1 p-0"
        :placeholder="placeholder"
        style="min-width: 100px; outline: none"
        @blur="onBlur"
        :name="'inputtag-' + name"
        :id="'inputtag-id-' + name"
        :autocomplete="autocomplete"
      />

      <div v-show="showDropdown" class="tag-input__dropdown" role="listbox">
        <div
          v-for="(opt, i) in filteredOptions"
          :key="getOptText(opt) + '_' + i"
          class="tag-input__option"
          :class="{ 'is-active': i === highlightedIndex }"
          role="option"
          @mousedown.prevent="selectOption(opt)"
          @mousemove="highlightedIndex = i"
        >
          <span v-html="highlightText(getOptLabel(opt), inputValue)"></span>
        </div>
        <div
          v-if="!filteredOptions.length"
          class="tag-input__empty"
          aria-disabled="true"
        >
          No contact found
        </div>
      </div>

      <div v-if="errorMessage" class="invalid-feedback d-block w-100">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InputTags",
  props: {
    value: { type: Array, default: () => [] },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "พิมพ์แล้วกด Enter" },
    isValidate: { type: Boolean, default: false },
    hideBorder: { type: Boolean, default: false },
    disabledFirstValue: { type: Boolean, default: false },
    emailValidation: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    name: { default: "inputtag" },
    autocomplete: { default: "otp" },

    options: { default: () => [] },
    limit: { required: false, default: 0, type: Number },
    textField: { type: String, default: "email" },

    minChars: { type: Number, default: 1 },
    maxSuggestions: { type: Number, default: 8 },
    caseInsensitive: { type: Boolean, default: true },
    allowNew: { type: Boolean, default: false },
    ignoreDuplicates: { type: Boolean, default: true },
    closeOnSelect: { type: Boolean, default: true },
  },
  data() {
    return {
      internalTags: [...this.value],
      inputValue: "",
      errorMessage: "",
      showDropdown: false,
      highlightedIndex: -1,
      _blurTimer: null,
      showAllOnFocus: false,
    };
  },
  watch: {
    value(newVal) {
      this.internalTags = [...newVal];
    },
  },
  computed: {
    filteredOptions() {
      const qRaw = this.inputValue || "";
      const q = this.caseInsensitive ? qRaw.toLowerCase() : qRaw;
      if (q.length < this.minChars && !this.showAllOnFocus) return [];

      const current = new Set(
        this.internalTags.map((t) =>
          this.caseInsensitive ? String(t).toLowerCase() : String(t)
        )
      );

      const items = this.options.map((o) => ({
        text: this.getOptText(o),
        raw: o,
      }));

      return items
        .filter(({ text }) => {
          if (!text) return false;
          const cmp = this.caseInsensitive ? text.toLowerCase() : text;
          const hit = cmp.includes(q);
          const dup = current.has(
            this.caseInsensitive ? text.toLowerCase() : text
          );
          return hit && (!this.ignoreDuplicates || !dup);
        })
        .slice(0, this.maxSuggestions)
        .map(({ raw }) => raw);
    },
  },
  methods: {
    getOptText(opt) {
      return typeof opt === "string" ? opt : (opt && opt[this.textField]) || "";
    },

    focusInput() {
      this.$refs.input && this.$refs.input.focus();
    },
    onInput() {
      this.clearError();
      const canShow =
        (!!this.inputValue && this.inputValue.length >= this.minChars) ||
        this.showAllOnFocus;
      this.showDropdown = canShow;
      this.highlightedIndex = this.filteredOptions.length ? 0 : -1;
    },
    onEnter() {
      if (this.showDropdown && this.highlightedIndex > -1) {
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
        return;
      }
      this.addTagFromInput();
    },
    move(step) {
      if (!this.filteredOptions.length) return;
      const max = this.filteredOptions.length - 1;
      let next = this.highlightedIndex + step;
      if (next < 0) next = max;
      if (next > max) next = 0;
      this.highlightedIndex = next;
    },
    selectOption(opt) {
      this.inputValue = this.getOptText(opt);
      this.addTagFromInput();
    },
    closeDropdown() {
      this.showDropdown = false;
      this.highlightedIndex = -1;
    },
    onBlur() {
      clearTimeout(this._blurTimer);
      this.addTagFromInput();
      this._blurTimer = setTimeout(() => {
        this.showAllOnFocus = false;

        this.closeDropdown();
      }, 120);
    },

    addTagFromInput() {
      const value = (this.inputValue || "").trim();
      if (!value) {
        this.closeDropdown();
        return;
      }

      if (!this.validateEmail(value)) {
        this.errorMessage = "Invalid email format.";
        return;
      }

      if (!this.allowNew) {
        const existsInList = this.options.some((o) => {
          const t = this.getOptText(o);
          return this.caseInsensitive
            ? t.toLowerCase() === value.toLowerCase()
            : t === value;
        });
        if (!existsInList) {
          this.inputValue = "";
          this.closeDropdown();
          return;
        }
      }

      const already = this.internalTags.some((t) =>
        this.caseInsensitive
          ? t.toLowerCase() === value.toLowerCase()
          : t === value
      );
      if (!already || !this.ignoreDuplicates) {
        if (this.internalTags.length == this.limit && this.limit != 0) {
          this.internalTags[this.limit - 1] = value;
          this.inputValue = "";
          return this.$emit("input", this.internalTags.slice());
        }
        this.internalTags.push(value);
        this.$emit("input", this.internalTags.slice());
      }

      this.inputValue = "";
      if (this.closeOnSelect) this.closeDropdown();
    },

    addTag() {
      this.addTagFromInput();
    },

    removeTag(index) {
      if (this.disabledFirstValue && index === 0) return;
      this.internalTags.splice(index, 1);
      this.$emit("input", this.internalTags);
    },

    handleKeydown(e) {
      if (this.disabledFirstValue && this.internalTags.length === 1) {
        return;
      }
      if (
        e.key === "Backspace" &&
        !this.inputValue &&
        this.internalTags.length
      ) {
        this.removeTag(this.internalTags.length - 1);
      }
    },

    validateEmail(email) {
      if (!this.emailValidation) return true;
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    },

    clearError() {
      this.errorMessage = "";
    },

    // ===== highlight helper =====
    highlightText(text, q) {
      if (!q) return this.escapeHtml(text);
      const escQ = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(escQ, this.caseInsensitive ? "ig" : "g");
      return this.escapeHtml(text).replace(
        re,
        (m) => `<mark>${this.escapeHtml(m)}</mark>`
      );
    },
    escapeHtml(s) {
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    onFocus() {
      console.log("object");
      this.clearError();
      this.showAllOnFocus = true;

      const current = new Set(
        this.internalTags.map((t) =>
          this.caseInsensitive ? String(t).toLowerCase() : String(t)
        )
      );
      const allFiltered = this.options
        .filter((o) => {
          const t = this.getOptText(o);
          if (!t) return false;
          return (
            !this.ignoreDuplicates ||
            !current.has(this.caseInsensitive ? t.toLowerCase() : t)
          );
        })
        .slice(0, this.maxSuggestions);
      console.log(allFiltered, this.filteredOptions);
      if (allFiltered.length) {
        this.showDropdown = true;
        this.highlightedIndex = 0;
      }
    },
    getOptLabel(opt) {
      if (typeof opt === "string") return opt;
      const email = (opt && opt[this.textField]) || "";
      const name = opt && opt.name ? opt.name : "";
      return name ? `${name} <${email}>` : email;
    },
  },
};
</script>

<style scoped lang="scss">
.badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 4px;
  &.disabled {
    background-color: #d8dbe0;
    opacity: 1;
    border: unset;
    min-height: 25px;
  }
}

.tag-input {
  position: relative;
}
.tag-input__dropdown {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  max-height: 220px;
  overflow: auto;
  padding: 4px;
}
.tag-input__option {
  padding: 8px 10px;
  border-radius: 6px;
  line-height: 1.2;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tag-input__option:hover,
.tag-input__option.is-active {
  background: #f3f6ff;
}
.tag-input__option mark {
  background: transparent;
  font-weight: 600;
  text-decoration: underline;
}
.tag-input__empty {
  padding: 8px 12px;
  font-size: 0.95rem;
  color: #6c757d;
  pointer-events: none;
}
</style>
